export type MapDrillBreadcrumb = {
	floorId?: string;
	floorName?: string;
	/** Other floors on this location, so the trail can switch floors. */
	floorOptions?: { id: string; label: string }[];
	id: string;
	label: string;
	level?: string;
	locationId?: string;
	locationName?: string;
	zoneId?: string;
	zoneName?: string;
};

import {
	DisplayMode,
	type GeoJSONLineString,
	type GeoJSONPolygon,
	LocationStatus,
	type ZoneBoundary,
	type ZoneBoundaryKind,
	type ZoneBoundaryMode,
} from "@sitescene/types";

import type {
	ShadowFloor,
	ShadowLocation,
	ShadowSensor,
	ShadowSensorEditDraft,
	ShadowZone,
	ShadowZoneEditDraft,
} from "../../types/location-map";

export type {
	ShadowSensorEditDraft,
	ShadowZoneEditDraft,
} from "../../types/location-map";

import {
	isPointInGeoJsonPolygon,
	isSimpleGeoJsonPolygon,
} from "@sitescene/shared";
import {
	coordinatesToMarkerConnectLineString,
	coordinatesToMarkerConnectPolygon,
	featureToGeoJsonPolygon,
	geoJsonPolygonToOpenLineString,
	normalizeGeoJsonPolygon,
	normalizeZoneBoundary,
	storedBoundaryToLineString,
} from "@sitescene/shared";
import { normalizeZoneColor } from "@sitescene/shared";

import { isValidCoordinatePair } from "../shared/coordinates";
import { toTitleCase } from "../shared/strings";
import { getRefId } from "./installation-ids";

/**
 * Helpers for the Location Map tab: search, focus/dim zones, and sensor edit drafts.
 *
 * Search matches zone or sensor names. Expanding a zone focuses the map on it.
 * Unfocused zones can be dimmed instead of hidden so admins keep context.
 *
 * Two edit layouts: the form follows the zone being edited, or stays in a
 * sticky panel beside the list.
 */

export const DEFAULT_ZONE_BORDER_WIDTH = 2;
export const MIN_ZONE_BORDER_WIDTH = 1;
export const MAX_ZONE_BORDER_WIDTH = 12;

export const normalizeZoneBorderWidth = (value: unknown): number => {
	const parsed = typeof value === "number" ? value : Number(value);
	if (!Number.isFinite(parsed)) return DEFAULT_ZONE_BORDER_WIDTH;

	return Math.min(
		MAX_ZONE_BORDER_WIDTH,
		Math.max(MIN_ZONE_BORDER_WIDTH, Math.round(parsed)),
	);
};

export const DEFAULT_ZONE_BOUNDARY_MODE: ZoneBoundaryMode = "auto";
export const DEFAULT_ZONE_BOUNDARY_KIND: ZoneBoundaryKind = "polygon";

export const ZONE_BOUNDARY_SELF_INTERSECTION_MESSAGE =
	"This outline crosses itself. Move vertices so edges don't cross.";

export const ZONE_BOUNDARY_MOVE_SENSORS_LABEL = "Move sensors with outline";

export const ZONE_BOUNDARY_MOVE_SENSORS_HINT =
	"Applies when you drag the whole outline. Editing vertices does not move sensors.";

export const ZONE_BOUNDARY_AUTO_MOVE_HINT =
	"Drag the outline on the map to move this zone's mapped sensors. Drag a vertex to switch to Custom and reshape.";

export const ZONE_BOUNDARY_CUSTOM_EDIT_HINT =
	"Drag the whole outline to move it, or drag vertices to reshape.";

export const ZONE_BOUNDARY_CUSTOM_DRAW_HINT =
	"Click the map to place vertices. Double-click or click the first point to finish.";

export const ZONE_BOUNDARY_CUSTOM_VERTEX_HINT =
	"Drag vertices on the map. Click edge midpoints to add anchors.";

export const ZONE_TYPE_LINE_HINT =
	"Open line — ends stay unconnected. Delete vertices to follow only some sides of a building. Sensors stay on the line.";

export const ZONE_TYPE_SHAPE_HINT =
	"A filled shape around the zone. Choose Auto or Custom below.";

export const ZONE_BOUNDARY_REDRAW_LABEL = "Redraw outline";

export const ZONE_BOUNDARY_REMOVE_LABEL = "Remove outline";

export const ZONE_BOUNDARY_EXPAND_TO_INCLUDE_LABEL =
	"Expand outline to include sensors";

export const getZoneBoundarySensorsOutsideMessage = (count: number): string =>
	count === 1
		? "1 sensor in this zone is outside the outline. You can still save."
		: `${count} sensors in this zone are outside the outline. You can still save.`;

export const getZoneBoundarySensorsOutsideAccordionTooltip = (
	count: number,
): string =>
	count === 1
		? "1 sensor in this zone is outside the zones boundary"
		: `${count} sensors in this zone are outside the zones boundary`;

export const SENSOR_MISSING_COORDINATES_TOOLTIP =
	"This sensor has no map coordinates";

export type ZoneSensorOutsideBoundary = {
	coordinates: [number, number];
	id: string;
	name: string;
};

export const isZoneEditDraftBoundarySelfIntersecting = (
	draft: ShadowZoneEditDraft,
): boolean => {
	if (!draft.boundary) return false;

	// Perimeter lines may zigzag; self-intersection is only a hard error for interiors.
	if (draft.boundaryKind === "line" || draft.boundary.type === "LineString") {
		return false;
	}

	if (draft.boundaryMode !== "custom") return false;

	const normalized = normalizeGeoJsonPolygon(draft.boundary);
	if (!normalized) return false;
	return !isSimpleGeoJsonPolygon(normalized);
};

export const getSensorsOutsideZoneBoundary = (
	sensors: ShadowSensor[],
	boundary: null | undefined | ZoneBoundary,
): ZoneSensorOutsideBoundary[] => {
	if (!boundary || boundary.type !== "Polygon") return [];

	const normalized = normalizeGeoJsonPolygon(boundary);
	if (!normalized) return [];

	return sensors.flatMap((sensor) => {
		const coordinates = sensor.geolocation?.coordinates;
		if (!isValidCoordinatePair(coordinates)) return [];
		if (isPointInGeoJsonPolygon(coordinates, normalized)) return [];

		return [
			{
				coordinates,
				id: sensor.id,
				name: sensor.name,
			},
		];
	});
};

export const getManualBoundaryForOutsideCheck = (
	zone: ShadowZone,
	options?: {
		draft?: null | ShadowZoneEditDraft;
		editingZoneId?: null | string;
	},
): GeoJSONPolygon | undefined => {
	if (options?.editingZoneId === zone.id && options.draft) {
		if (options.draft.boundaryMode !== "custom") return undefined;
		if (options.draft.boundaryKind === "line") return undefined;
		if (isZoneEditDraftBoundarySelfIntersecting(options.draft)) {
			return undefined;
		}
		return options.draft.boundary?.type === "Polygon"
			? options.draft.boundary
			: undefined;
	}

	if (resolveZoneBoundaryMode(zone) !== "custom") return undefined;
	if (resolveZoneBoundaryKind(zone) === "line") return undefined;
	return normalizeGeoJsonPolygon(zone.boundary) ?? undefined;
};

export const getSensorsOutsideZone = (
	zone: ShadowZone,
	options?: {
		draft?: null | ShadowZoneEditDraft;
		editingZoneId?: null | string;
	},
): ZoneSensorOutsideBoundary[] =>
	getSensorsOutsideZoneBoundary(
		zone.sensors ?? [],
		getManualBoundaryForOutsideCheck(zone, options),
	);

export const getZoneSensorCoordinates = (
	sensors: ShadowSensor[],
): [number, number][] =>
	sensors
		.map((sensor) => sensor.geolocation?.coordinates)
		.filter(isValidCoordinatePair)
		.sort(([lngA, latA], [lngB, latB]) =>
			lngA === lngB ? latA - latB : lngA - lngB,
		);

export type SensorLngLatById = Record<string, [number, number]>;

export const snapshotMappedSensorCoordinates = (
	sensors: ShadowSensor[],
): SensorLngLatById => {
	const coordinatesById: SensorLngLatById = {};

	for (const sensor of sensors) {
		const coordinates = sensor.geolocation?.coordinates;
		if (!isValidCoordinatePair(coordinates)) continue;
		coordinatesById[sensor.id] = [coordinates[0], coordinates[1]];
	}

	return coordinatesById;
};

const getBoundaryAnchor = (
	boundary: null | undefined | ZoneBoundary,
): [number, number] | undefined => {
	if (!boundary) return undefined;
	if (boundary.type === "LineString") {
		return boundary.coordinates[0];
	}
	return boundary.coordinates[0]?.[0];
};

export const getPolygonTranslationDelta = (
	previous: null | undefined | ZoneBoundary,
	next: null | undefined | ZoneBoundary,
): [number, number] | null => {
	const from = getBoundaryAnchor(previous);
	const to = getBoundaryAnchor(next);
	if (!from || !to || from.length < 2 || to.length < 2) return null;

	const deltaLng = to[0]! - from[0]!;
	const deltaLat = to[1]! - from[1]!;
	if (deltaLng === 0 && deltaLat === 0) return null;

	return [deltaLng, deltaLat];
};

const projectPointOntoSegment = (
	point: [number, number],
	start: [number, number],
	end: [number, number],
): { distanceSquared: number; point: [number, number] } => {
	const deltaLng = end[0] - start[0];
	const deltaLat = end[1] - start[1];
	if (deltaLng === 0 && deltaLat === 0) {
		const offsetLng = point[0] - start[0];
		const offsetLat = point[1] - start[1];
		return {
			distanceSquared: offsetLng * offsetLng + offsetLat * offsetLat,
			point: start,
		};
	}

	const lengthSquared = deltaLng * deltaLng + deltaLat * deltaLat;
	const projection =
		((point[0] - start[0]) * deltaLng + (point[1] - start[1]) * deltaLat) /
		lengthSquared;
	const clamped = Math.max(0, Math.min(1, projection));
	const closest: [number, number] = [
		start[0] + clamped * deltaLng,
		start[1] + clamped * deltaLat,
	];
	const offsetLng = point[0] - closest[0];
	const offsetLat = point[1] - closest[1];

	return {
		distanceSquared: offsetLng * offsetLng + offsetLat * offsetLat,
		point: closest,
	};
};

export const projectPointOntoLineString = (
	point: [number, number],
	line: GeoJSONLineString,
): [number, number] | null => {
	const coordinates = line.coordinates;
	if (coordinates.length === 0) return null;
	if (coordinates.length === 1) return coordinates[0] ?? null;

	let best = projectPointOntoSegment(point, coordinates[0]!, coordinates[1]!);

	for (let index = 1; index < coordinates.length - 1; index += 1) {
		const candidate = projectPointOntoSegment(
			point,
			coordinates[index]!,
			coordinates[index + 1]!,
		);
		if (candidate.distanceSquared < best.distanceSquared) {
			best = candidate;
		}
	}

	return best.point;
};

export const snapSensorLngLatByIdToLine = (
	coordinatesById: SensorLngLatById,
	line: GeoJSONLineString | null | undefined,
): SensorLngLatById => {
	if (!line || line.coordinates.length < 2) return coordinatesById;

	const next: SensorLngLatById = {};
	for (const [sensorId, coordinates] of Object.entries(coordinatesById)) {
		next[sensorId] =
			projectPointOntoLineString(coordinates, line) ?? coordinates;
	}

	return next;
};

export const translateSensorLngLatById = (
	coordinatesById: SensorLngLatById,
	delta: [number, number],
): SensorLngLatById => {
	const next: SensorLngLatById = {};

	for (const [sensorId, coordinates] of Object.entries(coordinatesById)) {
		next[sensorId] = [coordinates[0] + delta[0], coordinates[1] + delta[1]];
	}

	return next;
};

export const areSensorLngLatByIdEqual = (
	left: SensorLngLatById,
	right: SensorLngLatById,
): boolean => {
	const leftIds = Object.keys(left).sort();
	const rightIds = Object.keys(right).sort();
	if (leftIds.length !== rightIds.length) return false;

	return leftIds.every((sensorId) => {
		const leftCoordinates = left[sensorId];
		const rightCoordinates = right[sensorId];
		return (
			!!leftCoordinates &&
			!!rightCoordinates &&
			leftCoordinates[0] === rightCoordinates[0] &&
			leftCoordinates[1] === rightCoordinates[1]
		);
	});
};

export const applySensorCoordinatesToSensors = (
	sensors: ShadowSensor[],
	coordinatesById: SensorLngLatById,
): ShadowSensor[] =>
	sensors.map((sensor) => {
		const coordinates = coordinatesById[sensor.id];
		if (!coordinates) return sensor;

		return {
			...sensor,
			geolocation: {
				coordinates,
				type: "Point",
			},
		};
	});

export const applySensorCoordinateOverridesToMarkers = (
	markers: GeoJSON.Feature[],
	coordinatesById: SensorLngLatById,
): GeoJSON.Feature[] => {
	if (Object.keys(coordinatesById).length === 0) return markers;

	return markers.map((marker) => {
		const sensorId = String(marker.id ?? "");
		const nextTrue = coordinatesById[sensorId];
		if (!nextTrue || marker.geometry.type !== "Point") return marker;

		const currentTrue = marker.properties?.trueCoordinates as
			| [number, number]
			| undefined;
		const display = marker.geometry.coordinates as [number, number];
		if (
			currentTrue &&
			currentTrue[0] === nextTrue[0] &&
			currentTrue[1] === nextTrue[1]
		) {
			return marker;
		}

		const offsetLng = currentTrue ? display[0] - currentTrue[0] : 0;
		const offsetLat = currentTrue ? display[1] - currentTrue[1] : 0;

		return {
			...marker,
			geometry: {
				...marker.geometry,
				coordinates: [nextTrue[0] + offsetLng, nextTrue[1] + offsetLat],
			},
			properties: {
				...marker.properties,
				trueCoordinates: nextTrue,
			},
		};
	});
};

export const resolveZoneBoundaryMode = (zone: ShadowZone): ZoneBoundaryMode => {
	const storedMode = zone.boundaryMode as string | undefined;
	if (storedMode === "auto") return "auto";
	// Legacy documents stored custom outlines as "manual".
	if (storedMode === "custom" || storedMode === "manual") return "custom";

	const boundary = zone.boundary;
	if (boundary == null || typeof boundary !== "object") {
		return DEFAULT_ZONE_BOUNDARY_MODE;
	}

	if (
		!Array.isArray(boundary) &&
		!("coordinates" in boundary) &&
		!("type" in boundary)
	) {
		return DEFAULT_ZONE_BOUNDARY_MODE;
	}

	return normalizeZoneBoundary(boundary)
		? "custom"
		: DEFAULT_ZONE_BOUNDARY_MODE;
};

export const resolveZoneBoundaryKind = (zone: ShadowZone): ZoneBoundaryKind => {
	if (zone.boundaryKind === "line" || zone.boundaryKind === "polygon") {
		return zone.boundaryKind;
	}

	if (zone.boundary?.type === "LineString") return "line";
	return DEFAULT_ZONE_BOUNDARY_KIND;
};

export const polygonToLineStringBoundary = (
	polygon: GeoJSONPolygon,
): GeoJSONLineString | undefined =>
	geoJsonPolygonToOpenLineString(polygon) ?? undefined;

export const lineStringToPolygonBoundary = (
	line: GeoJSONLineString,
): GeoJSONPolygon | undefined => {
	if (line.coordinates.length < 3) return undefined;

	const first = line.coordinates[0];
	const last = line.coordinates[line.coordinates.length - 1];
	if (!first) return undefined;

	const ring =
		last && last[0] === first[0] && last[1] === first[1]
			? line.coordinates
			: [...line.coordinates, first];

	return { coordinates: [ring], type: "Polygon" };
};

export const convertZoneBoundaryToKind = (
	boundary: null | undefined | ZoneBoundary,
	kind: ZoneBoundaryKind,
): null | undefined | ZoneBoundary => {
	if (!boundary) return boundary;

	if (kind === "line") {
		if (boundary.type === "LineString") return boundary;
		return polygonToLineStringBoundary(boundary) ?? null;
	}

	if (boundary.type === "Polygon") return boundary;
	return lineStringToPolygonBoundary(boundary) ?? null;
};

/** Prefer the polyline with more vertices so a saved perimeter is not replaced by marker-connect. */
export const pickRicherLineString = (
	...candidates: Array<GeoJSONLineString | null | undefined>
): GeoJSONLineString | undefined => {
	let best: GeoJSONLineString | undefined;

	for (const candidate of candidates) {
		if (!candidate || candidate.coordinates.length < 2) continue;
		if (!best || candidate.coordinates.length > best.coordinates.length) {
			best = candidate;
		}
	}

	return best;
};

export const seedManualZoneBoundary = (
	zone: ShadowZone,
): undefined | ZoneBoundary => {
	const kind = resolveZoneBoundaryKind(zone);

	if (zone.boundary) {
		const normalized = normalizeZoneBoundary(zone.boundary);
		if (normalized) {
			return convertZoneBoundaryToKind(normalized, kind) ?? undefined;
		}
	}

	if (kind === "line") {
		if (zone.boundary) return undefined;

		const sensorCoordinates = getZoneSensorCoordinates(zone.sensors ?? []);
		return coordinatesToMarkerConnectLineString(sensorCoordinates) ?? undefined;
	}

	const sensorCoordinates = getZoneSensorCoordinates(zone.sensors ?? []);
	const feature = coordinatesToMarkerConnectPolygon(sensorCoordinates);
	return feature ? (featureToGeoJsonPolygon(feature) ?? undefined) : undefined;
};

export const SENSOR_TYPE_OPTIONS = [
	{ id: "camera", label: "Camera" },
	{ id: "motion", label: "Motion" },
	{ id: "air_quality", label: "Air Quality" },
] as const;

export const EMPTY_ZONES: ShadowZone[] = [];

/** Stable placeholder when the org has no locations (overview helpers still need a Location). */
export const EMPTY_MAP_LOCATION: ShadowLocation = {
	createdAt: new Date(0),
	displayMode: DisplayMode.ROADMAP,
	floors: [],
	id: "",
	name: "",
	status: LocationStatus.INACTIVE,
	updatedAt: new Date(0),
	zones: [],
};

export const sortZonesByName = (zones: ShadowZone[]): ShadowZone[] =>
	[...zones].sort((a, b) => a.name.localeCompare(b.name));

/**
 * Map edit accordion order: lower `sortOrder` first (visually in front on the map).
 * Zones without `sortOrder` sort after numbered ones, then by name.
 */
export const sortZonesForMapList = (zones: ShadowZone[]): ShadowZone[] =>
	[...zones].sort((a, b) => {
		const aOrder =
			typeof a.sortOrder === "number" && Number.isFinite(a.sortOrder)
				? a.sortOrder
				: Number.POSITIVE_INFINITY;
		const bOrder =
			typeof b.sortOrder === "number" && Number.isFinite(b.sortOrder)
				? b.sortOrder
				: Number.POSITIVE_INFINITY;
		if (aOrder !== bOrder) return aOrder - bOrder;
		return a.name.localeCompare(b.name);
	});

/**
 * Floor list order: lower `sortOrder` first.
 * Floors without `sortOrder` sort after numbered ones, then by name.
 * This order is the zoom order: the top of the list is the first zoom level.
 */
export const sortFloorsForList = (
	floors: readonly ShadowFloor[],
): ShadowFloor[] =>
	[...floors].sort((a, b) => {
		const aOrder =
			typeof a.sortOrder === "number" && Number.isFinite(a.sortOrder)
				? a.sortOrder
				: Number.POSITIVE_INFINITY;
		const bOrder =
			typeof b.sortOrder === "number" && Number.isFinite(b.sortOrder)
				? b.sortOrder
				: Number.POSITIVE_INFINITY;
		if (aOrder !== bOrder) return aOrder - bOrder;
		return a.name.localeCompare(b.name);
	});

/** Floors whose `sortOrder` should change to match `orderedFloorIds` (0..n-1). */
export const getFloorSortOrderUpdates = (
	floors: ShadowFloor[],
	orderedFloorIds: string[],
): Array<{ floor: ShadowFloor; sortOrder: number }> => {
	const byId = new Map(floors.map((floor) => [floor.id, floor]));
	const updates: Array<{ floor: ShadowFloor; sortOrder: number }> = [];

	orderedFloorIds.forEach((floorId, index) => {
		const floor = byId.get(floorId);
		if (!floor) return;
		if (floor.sortOrder === index) return;
		updates.push({ floor, sortOrder: index });
	});

	return updates;
};

export type MapFloorScope = {
	/** Floor on the map. Null when the selection is a zone that is not on a floor. */
	floorId: null | string;
	zoneIds: ReadonlySet<string>;
};

const floorIdOnLocation = (
	zone: undefined | { floor?: unknown },
	floorIds: ReadonlySet<string>,
): null | string | undefined => {
	if (!zone) return undefined;
	const floorId = getRefId(zone.floor);
	if (!floorId || !floorIds.has(floorId)) return null;
	return floorId;
};

/**
 * Which floor the map should draw.
 * A zone or sensor selection shows that zone's current floor and every zone
 * on it. With nothing selected, the floor with the lowest `sortOrder` is used.
 * Locations without floors return null so the map keeps showing every zone.
 */
export const resolveMapFloorScope = ({
	editingSensorZoneId,
	editingZoneId,
	floors,
	highlightedFloorId,
	openZoneId,
	zones,
}: {
	editingSensorZoneId?: null | string;
	editingZoneId?: null | string;
	floors: readonly ShadowFloor[];
	highlightedFloorId?: null | string;
	openZoneId?: null | string;
	zones: readonly { floor?: unknown; id: string }[];
}): MapFloorScope | null => {
	const orderedFloors = sortFloorsForList(floors);
	if (orderedFloors.length === 0) return null;

	const floorIds = new Set(orderedFloors.map((floor) => floor.id));
	const zoneById = new Map(zones.map((zone) => [zone.id, zone]));
	const zonesForFloor = (floorId: string) =>
		new Set(
			zones
				.filter((zone) => floorIdOnLocation(zone, floorIds) === floorId)
				.map((zone) => zone.id),
		);

	const scopeForZone = (zoneId: string): "missing" | MapFloorScope => {
		const floorId = floorIdOnLocation(zoneById.get(zoneId), floorIds);
		if (floorId === undefined) return "missing";
		if (floorId === null) {
			return { floorId: null, zoneIds: new Set([zoneId]) };
		}
		return { floorId, zoneIds: zonesForFloor(floorId) };
	};

	const selectedZoneId = editingZoneId || editingSensorZoneId || openZoneId;
	if (selectedZoneId) {
		const scope = scopeForZone(selectedZoneId);
		if (scope !== "missing") return scope;
	}

	if (highlightedFloorId && floorIds.has(highlightedFloorId)) {
		return {
			floorId: highlightedFloorId,
			zoneIds: zonesForFloor(highlightedFloorId),
		};
	}

	const firstFloorId = orderedFloors[0]?.id;
	if (!firstFloorId) return null;
	return { floorId: firstFloorId, zoneIds: zonesForFloor(firstFloorId) };
};

export const sortSensorsByName = (sensors: ShadowSensor[]): ShadowSensor[] =>
	[...sensors].sort((a, b) => a.name.localeCompare(b.name));

export type LocationMapZoneSearchEntry = {
	matchedBy: "pinned" | "sensor" | "zone";
	sensors: ShadowSensor[];
	zone: ShadowZone;
	zoneIndex: number;
};

const nameMatchesSearch = (name: string, query: string): boolean => {
	const normalizedQuery = query.toLowerCase().trim();
	if (!normalizedQuery) return true;

	return (
		name.toLowerCase().includes(normalizedQuery) ||
		toTitleCase(name).toLowerCase().includes(normalizedQuery)
	);
};

/**
 * Filter the zone list by name. A zone stays visible if its name matches,
 * any of its sensors match, or it is currently being edited (pinned).
 */
export const filterLocationMapZonesBySearch = (
	zones: ShadowZone[],
	searchQuery: string,
	pinnedZoneIds: ReadonlySet<string> = new Set(),
): LocationMapZoneSearchEntry[] => {
	const query = searchQuery.toLowerCase().trim();

	return zones.flatMap((zone, zoneIndex): LocationMapZoneSearchEntry[] => {
		const sensors = sortSensorsByName(zone.sensors ?? []);
		const isPinned = pinnedZoneIds.has(zone.id);

		if (!query) {
			return [{ matchedBy: "zone" as const, sensors, zone, zoneIndex }];
		}

		const zoneMatches = nameMatchesSearch(zone.name, query);
		const matchingSensors = sensors.filter((sensor) =>
			nameMatchesSearch(sensor.name, query),
		);

		if (zoneMatches) {
			return [{ matchedBy: "zone" as const, sensors, zone, zoneIndex }];
		}

		if (matchingSensors.length > 0) {
			return [
				{
					matchedBy: "sensor" as const,
					sensors: matchingSensors,
					zone,
					zoneIndex,
				},
			];
		}

		if (isPinned) {
			return [{ matchedBy: "pinned" as const, sensors, zone, zoneIndex }];
		}

		return [];
	});
};

export const getLocationMapFilteredAccordionOpenPanels = (
	entries: LocationMapZoneSearchEntry[],
	openPanels: Set<number>,
	searchQuery: string,
): Set<number> => {
	const isSearching = searchQuery.trim().length > 0;
	const next = new Set<number>();

	entries.forEach((entry, filteredIndex) => {
		if (openPanels.has(entry.zoneIndex)) {
			next.add(filteredIndex);
			return;
		}

		if (isSearching && entry.matchedBy === "sensor") {
			next.add(filteredIndex);
		}
	});

	return next;
};

export const mapFilteredAccordionOpenPanelsToOriginal = (
	nextFiltered: Set<number>,
	entries: LocationMapZoneSearchEntry[],
	currentOpenPanels: Set<number>,
): Set<number> => {
	const nextOriginal = new Set<number>();

	for (const filteredIndex of nextFiltered) {
		const zoneIndex = entries[filteredIndex]?.zoneIndex;
		if (zoneIndex !== undefined) {
			nextOriginal.add(zoneIndex);
		}
	}

	for (const zoneIndex of currentOpenPanels) {
		const isVisible = entries.some((entry) => entry.zoneIndex === zoneIndex);
		if (!isVisible) {
			nextOriginal.add(zoneIndex);
		}
	}

	return nextOriginal;
};

export const findSensorInLocation = (
	location: ShadowLocation,
	sensorId: string,
): undefined | { sensor: ShadowSensor; zoneId: string } => {
	for (const zone of location.zones ?? []) {
		const sensor = zone.sensors?.find((item) => item.id === sensorId);
		if (sensor) {
			return { sensor, zoneId: zone.id };
		}
	}
	return undefined;
};

export const buildLocationMapBreadcrumbs = ({
	floors = [],
	focusedFloor,
	focusedZone,
	location,
	selectedSensor,
}: {
	floors?: ShadowFloor[];
	focusedFloor?: ShadowFloor;
	focusedZone?: ShadowZone;
	location: ShadowLocation;
	selectedSensor?: ShadowSensor;
}): MapDrillBreadcrumb[] => {
	if (!focusedFloor && !focusedZone) return [];

	const floorOptions =
		floors.length > 1
			? floors.map((floor) => ({
					id: floor.id,
					label: toTitleCase(floor.name),
				}))
			: undefined;

	const crumbs: MapDrillBreadcrumb[] = [
		{
			id: `location:${location.id}`,
			label: toTitleCase(location.name),
			level: "zones",
			locationId: location.id,
			locationName: location.name,
		},
	];

	if (focusedFloor) {
		crumbs.push({
			floorId: focusedFloor.id,
			floorName: focusedFloor.name,
			floorOptions,
			id: `floor:${focusedFloor.id}`,
			label: toTitleCase(focusedFloor.name),
			level: "floors",
			locationId: location.id,
			locationName: location.name,
		});
	}

	if (!focusedZone) return crumbs;

	crumbs.push({
		floorId: focusedFloor?.id,
		floorName: focusedFloor?.name,
		id: `zone:${focusedZone.id}`,
		label: toTitleCase(focusedZone.name),
		level: "sensors",
		locationId: location.id,
		locationName: location.name,
		zoneId: focusedZone.id,
		zoneName: focusedZone.name,
	});

	if (!selectedSensor) return crumbs;

	crumbs.push({
		floorId: focusedFloor?.id,
		floorName: focusedFloor?.name,
		id: `sensor:${selectedSensor.id}`,
		label: toTitleCase(selectedSensor.name),
		level: "sensors",
		locationId: location.id,
		locationName: location.name,
		zoneId: focusedZone.id,
		zoneName: focusedZone.name,
	});

	return crumbs;
};

export const getMarkerZoneId = (
	feature: GeoJSON.Feature,
): string | undefined =>
	typeof feature.properties?.zoneId === "string"
		? feature.properties.zoneId
		: undefined;

export const getOverlayZoneId = (
	feature: GeoJSON.Feature,
): string | undefined => {
	const id = feature.id;
	if (typeof id === "string" && id.startsWith("zone-bounds:")) {
		return id.slice("zone-bounds:".length);
	}

	const zoneId = feature.properties?.zoneId;
	return typeof zoneId === "string" ? zoneId : undefined;
};

/**
 * Fade pins and overlays that are not in the focused zone, instead of hiding
 * them, so admins still see the rest of the location while editing one zone.
 */
export const applyZoneFocusToMapFeatures = (
	features: GeoJSON.Feature[] | undefined,
	focusedZoneIds: null | Set<string>,
	getZoneId: (feature: GeoJSON.Feature) => string | undefined,
	highlightedFloorId?: null | string,
): GeoJSON.Feature[] => {
	if (!features) return [];
	if (!focusedZoneIds) return features;

	return features.map((feature) => {
		const zoneId = getZoneId(feature);
		const isFloorOutline = feature.properties?.overlayKind === "floor";
		const featureFloorId =
			typeof feature.properties?.floorId === "string"
				? feature.properties.floorId
				: undefined;
		const isFocused =
			(isFloorOutline &&
				(!highlightedFloorId || featureFloorId === highlightedFloorId)) ||
			(zoneId !== undefined && focusedZoneIds.has(zoneId));

		if (isFocused) {
			return feature;
		}

		return {
			...feature,
			properties: {
				...feature.properties,
				dimmed: true,
			},
		};
	});
};

export const filterMapFeaturesByZones = (
	features: GeoJSON.Feature[] | undefined,
	focusedZoneIds: null | Set<string>,
	getZoneId: (feature: GeoJSON.Feature) => string | undefined,
): GeoJSON.Feature[] | undefined => {
	if (!features || !focusedZoneIds) return features;

	return features.filter((feature) => {
		if (feature.properties?.overlayKind === "floor") return true;
		const zoneId = getZoneId(feature);
		return zoneId !== undefined && focusedZoneIds.has(zoneId);
	});
};

export const focusMapFeaturesByZones = (
	features: GeoJSON.Feature[] | undefined,
	focusedZoneIds: null | Set<string>,
	getZoneId: (feature: GeoJSON.Feature) => string | undefined,
	dimUnfocusedZones = false,
	highlightedFloorId?: null | string,
): GeoJSON.Feature[] | undefined => {
	if (dimUnfocusedZones) {
		return applyZoneFocusToMapFeatures(
			features,
			focusedZoneIds,
			getZoneId,
			highlightedFloorId,
		);
	}

	return filterMapFeaturesByZones(features, focusedZoneIds, getZoneId);
};

export const coordinatesEqual = (
	a: [number, number] | undefined,
	b: [number, number] | undefined,
): boolean => {
	if (!a || !b) return a === b;
	return a[0] === b[0] && a[1] === b[1];
};

export const getCoordinateStringsFromSensor = (
	sensor: ShadowSensor,
): { latitude: string; longitude: string } => {
	const coordinates = sensor.geolocation?.coordinates;
	if (!isValidCoordinatePair(coordinates)) {
		return { latitude: "", longitude: "" };
	}

	return {
		latitude: String(coordinates[1]),
		longitude: String(coordinates[0]),
	};
};

export const createZoneEditDraft = (
	zone: ShadowZone,
	options?: { perimeterLine?: GeoJSONLineString },
): ShadowZoneEditDraft => {
	const boundaryKind = resolveZoneBoundaryKind(zone);
	const boundaryMode =
		boundaryKind === "line" ? "custom" : resolveZoneBoundaryMode(zone);

	const storedLine = storedBoundaryToLineString(zone.boundary);
	const storedShape = normalizeZoneBoundary(zone.boundary);
	const perimeterBoundary =
		boundaryKind === "line"
			? (pickRicherLineString(options?.perimeterLine, storedLine) ?? null)
			: boundaryMode === "custom"
				? (storedShape ?? null)
				: undefined;

	return {
		borderWidth: normalizeZoneBorderWidth(zone.borderWidth),
		boundary: perimeterBoundary,
		boundaryKind,
		boundaryMode,
		color: normalizeZoneColor(zone.color),
		floorId: getRefId(zone.floor) ?? "",
		name: toTitleCase(zone.name),
	};
};

export const buildZoneUpdateFromEditDraft = (
	draft: ShadowZoneEditDraft,
): {
	borderWidth: number;
	boundary?: null | ZoneBoundary;
	boundaryKind: ZoneBoundaryKind;
	boundaryMode: ZoneBoundaryMode;
	color: string;
	floor: null | string;
	name: string;
} => {
	const update = {
		borderWidth: normalizeZoneBorderWidth(draft.borderWidth),
		boundaryKind: draft.boundaryKind,
		boundaryMode: draft.boundaryMode,
		color: normalizeZoneColor(draft.color),
		floor: draft.floorId || null,
		name: draft.name.trim(),
	};

	if (draft.boundaryKind === "line") {
		const line = storedBoundaryToLineString(draft.boundary);

		if (line) {
			return {
				...update,
				boundary: line,
				boundaryMode: "custom",
			};
		}

		if (draft.boundary === null) {
			return {
				...update,
				boundary: null,
				boundaryMode: "custom",
			};
		}

		return {
			...update,
			boundaryMode: "custom",
		};
	}

	if (draft.boundaryMode === "auto") {
		return {
			...update,
			boundary: null,
		};
	}

	if (draft.boundaryMode === "custom") {
		if (draft.boundary) {
			return {
				...update,
				boundary: normalizeZoneBoundary(draft.boundary) ?? draft.boundary,
			};
		}

		if (draft.boundary === null) {
			return {
				...update,
				boundary: null,
			};
		}

		// Manual mode without a polygon yet (e.g. just switched from Auto).
		// Avoid boundary: null so auto-save does not clear an existing boundary.
		return update;
	}

	return {
		...update,
		boundary: null,
	};
};

export const createSensorEditDraft = (
	sensor: ShadowSensor,
	zoneId: string,
): ShadowSensorEditDraft => {
	const { latitude, longitude } = getCoordinateStringsFromSensor(sensor);

	return {
		latitude,
		longitude,
		name: sensor.name,
		status: sensor.status,
		type: sensor.type,
		zoneId,
	};
};

export const parseSensorEditDraftCoordinates = (
	draft: Pick<ShadowSensorEditDraft, "latitude" | "longitude">,
): [number, number] | undefined => {
	const lat = parseFloat(draft.latitude);
	const lng = parseFloat(draft.longitude);
	return isValidCoordinatePair([lng, lat]) ? [lng, lat] : undefined;
};

export const getSensorEditDraftCoordinateDirty = (
	draft: Pick<ShadowSensorEditDraft, "latitude" | "longitude">,
	sensor: ShadowSensor,
): boolean => {
	const saved = getCoordinateStringsFromSensor(sensor);
	return (
		draft.latitude.trim() !== saved.latitude.trim() ||
		draft.longitude.trim() !== saved.longitude.trim()
	);
};

export const isSensorEditDraftCoordinatesValid = (
	draft: Pick<ShadowSensorEditDraft, "latitude" | "longitude">,
): boolean => parseSensorEditDraftCoordinates(draft) !== undefined;

/** Compares draft field values as typed — unlike fingerprint, does not trim. */
export const areSensorEditDraftsEqual = (
	left: ShadowSensorEditDraft,
	right: ShadowSensorEditDraft,
): boolean =>
	left.name === right.name &&
	left.status === right.status &&
	left.type === right.type &&
	left.zoneId === right.zoneId &&
	left.latitude === right.latitude &&
	left.longitude === right.longitude;

export const getSensorEditDraftFingerprint = (
	draft: ShadowSensorEditDraft,
): string =>
	JSON.stringify({
		latitude: draft.latitude.trim(),
		longitude: draft.longitude.trim(),
		name: draft.name.trim(),
		status: draft.status,
		type: draft.type,
		zoneId: draft.zoneId,
	});

export const areZoneEditDraftsEqual = (
	left: ShadowZoneEditDraft,
	right: ShadowZoneEditDraft,
): boolean =>
	left.boundaryMode === right.boundaryMode &&
	left.boundaryKind === right.boundaryKind &&
	left.borderWidth === right.borderWidth &&
	left.name === right.name &&
	left.color.toUpperCase() === right.color.toUpperCase() &&
	left.floorId === right.floorId &&
	left.boundary?.type === right.boundary?.type &&
	JSON.stringify(left.boundary?.coordinates ?? null) ===
		JSON.stringify(right.boundary?.coordinates ?? null);

export const getZoneEditDraftFingerprint = (
	draft: ShadowZoneEditDraft,
): string =>
	JSON.stringify({
		borderWidth: normalizeZoneBorderWidth(draft.borderWidth),
		boundary: draft.boundary?.coordinates ?? null,
		boundaryKind: draft.boundaryKind,
		boundaryMode: draft.boundaryMode,
		boundaryType: draft.boundary?.type ?? null,
		color: draft.color.trim().toUpperCase(),
		floorId: draft.floorId,
		name: draft.name.trim(),
	});

export const canPersistZoneEditDraft = (draft: ShadowZoneEditDraft): boolean =>
	draft.name.trim().length > 0 &&
	!isZoneEditDraftBoundarySelfIntersecting(draft);

export const canPersistSensorEditDraft = (
	draft: ShadowSensorEditDraft,
): boolean => {
	if (!draft.name.trim() || !draft.zoneId) return false;

	const hasPartialCoordinates =
		(draft.latitude.trim() !== "" || draft.longitude.trim() !== "") &&
		!isSensorEditDraftCoordinatesValid(draft);

	return !hasPartialCoordinates;
};

export const buildSensorUpdateFromEditDraft = (
	draft: ShadowSensorEditDraft,
): Partial<ShadowSensor> => {
	const sensorData: Partial<ShadowSensor> = {
		name: draft.name.trim(),
		status: draft.status,
		type: draft.type,
		zone: draft.zoneId,
	};

	const coordinates = parseSensorEditDraftCoordinates(draft);
	if (coordinates) {
		sensorData.geolocation = {
			coordinates,
			type: "Point",
		};
	} else if (draft.latitude.trim() === "" && draft.longitude.trim() === "") {
		// Explicit clear — both fields empty means remove map position.
		sensorData.geolocation = null;
	}

	return sensorData;
};
